import { assertUnreachable } from '@ftr/contracts/shared/assertUnreachable'
import { DateTimeFormatter } from '@js-joda/core'
import { Locale } from '@js-joda/locale'

/**
 * Date formats which can be used with DateComponent and DatePipe.
 *
 * @see DateComponent
 * @see DatePipe
 */
export enum DateFormat {
  /**
   * @example "1:23 AM"
   */
  Time = 'time',
  /**
   * @example "01:23 AM"
   */
  TimeLeadingZero = 'timeLeadingZero',
  /**
   * @example "10:23:00 AM"
   */
  TimeWithSeconds = 'timeWithSeconds',
  /**
   * @example "23:49:10.216", "09:19:10.314"
   */
  Time24HourWithMilliseconds = 'time24HourWithMilliseconds',
  /**
   * @example "23:49:10", "9:19:10"
   */
  TimeTwentyFourHourWithSeconds = 'timeTwentyFourHourWithSeconds',
  /**
   * @example "08:49"
   */
  TimeTwentyFourHourPadded = 'timeTwentyFourHourPadded',
  /**
   * @example "Oct 04, 2019"
   */
  Date = 'date',
  /**
   * @example "04 Oct 2019"
   */
  DateDayFirst = 'dateDayFirst',
  /**
   * @example "17 Feb, 2025"
   */
  DateDayShortMonthCommaYear = 'dateDayShortMonthCommaYear',
  /**
   * @example "04 October, 2019"
   */
  DateLong = 'dateLong',
  /**
   * @example "04 October 2019"
   */
  DateLongNoComma = 'dateLongNoComma',
  /**
   * @example "04 October 2019, Thursday"
   */
  DateWithDay = 'dateWithDay',
  /**
   * @example "Fri, Jan 26"
   */
  DayOfWeekMonthDay = 'dayOfWeekMonthDay',
  /**
   * @example "Friday Jan 26, 2025"
   */
  FullDayOfWeekMonthDayYear = 'fullDayOfWeekMonthDayYear',
  /**
   * @example "04 October, 2019 10:23 AM"
   */
  DateAndTime = 'dateAndTime',
  /**
   * @example "Oct 04, 2019 10:23 AM"
   */
  DateAndTimeShorter = 'dateAndTimeShorter',
  /**
   * @example "10/04/2019 10:23 AM"
   */
  DateAndTimeShort = 'dateAndTimeShort',
  /**
   * @example "10/04/2019 10:23 AM"
   */
  DateShort = 'dateShort',
  /**
   * @example "Friday"
   */
  DayOfWeek = 'dayOfWeek',

  /**
   * @example 2019-07-09T12:00:00.000
   */
  IsoLocalDateTime = 'isoLocal',

  /**
   * @example 2019-07-09T12:00:00.000Z
   */
  IsoZonedDateTime = 'iso',
  /**
   * @example Apr 05, 2019
   */
  MonthDayYearShort = 'monthDayYearShort',
  /**
   * @example Tue
   */
  ShortDayOfWeek = 'shortDayOfWeek',
  /**
   * @example 2019-12-04
   */
  UniversalDate = 'universalDate',
  /**
   * @example Thu Jun 26 2025 13:49:27 GMT+10:00
   */
  JavaScriptDateString = 'jsDateString',
}

export const ALL_DATE_FORMATS = Object.values(DateFormat) as DateFormat[]

/**
 * Given a date format, returns a joda formatter string which is can be used for
 * converting a date into the desired format. This has the locale as it's required
 * for formats like am/pm, day of week etc.
 */
export function jodaFormatPatternWithLocale(desiredFormat: DateFormat): DateTimeFormatter {
  return jodaFormatPattern(desiredFormat).withLocale(Locale.US)
}

/**
 * Given a date format, returns a joda formatter string which is can be used for
 * converting a date into the desired format.
 */
// eslint-disable-next-line complexity
function jodaFormatPattern(desiredFormat: DateFormat): DateTimeFormatter {
  switch (desiredFormat) {
    case DateFormat.Time:
      return DateTimeFormatter.ofPattern('h:mm a')
    case DateFormat.TimeLeadingZero:
      return DateTimeFormatter.ofPattern('hh:mm a')
    case DateFormat.TimeWithSeconds:
      return DateTimeFormatter.ofPattern('h:mm:ss a')
    case DateFormat.Time24HourWithMilliseconds:
      return DateTimeFormatter.ofPattern('HH:mm:ss.SSS')
    case DateFormat.TimeTwentyFourHourWithSeconds:
      return DateTimeFormatter.ofPattern('H:mm:ss')
    case DateFormat.TimeTwentyFourHourPadded:
      return DateTimeFormatter.ofPattern('HH:mm')
    case DateFormat.Date:
      return DateTimeFormatter.ofPattern('MMM dd, y')
    case DateFormat.DateDayFirst:
      return DateTimeFormatter.ofPattern('dd MMM y')
    case DateFormat.DateDayShortMonthCommaYear:
      return DateTimeFormatter.ofPattern('d MMM, y')
    case DateFormat.DateLong:
      return DateTimeFormatter.ofPattern('dd MMMM, y')
    case DateFormat.DateLongNoComma:
      return DateTimeFormatter.ofPattern('dd MMMM y')
    case DateFormat.DateWithDay:
      return DateTimeFormatter.ofPattern('dd MMMM y, eeee')
    case DateFormat.DayOfWeekMonthDay:
      return DateTimeFormatter.ofPattern('eee, MMM dd')
    case DateFormat.FullDayOfWeekMonthDayYear:
      return DateTimeFormatter.ofPattern('eeee MMM dd, yyyy')
    case DateFormat.DateAndTime:
      return DateTimeFormatter.ofPattern('dd MMMM, y h:mm a')
    case DateFormat.DateAndTimeShorter:
      return DateTimeFormatter.ofPattern('MMM dd, y h:mm a')
    case DateFormat.DateAndTimeShort:
      return DateTimeFormatter.ofPattern('MM/dd/y h:mm a')
    case DateFormat.DateShort:
      return DateTimeFormatter.ofPattern('MM/dd/y')
    case DateFormat.DayOfWeek:
      return DateTimeFormatter.ofPattern('eeee')
    case DateFormat.ShortDayOfWeek:
      return DateTimeFormatter.ofPattern('E')
    case DateFormat.IsoLocalDateTime:
      return DateTimeFormatter.ofPattern("y-MM-dd'T'HH:mm:ss.SSS")
    case DateFormat.IsoZonedDateTime:
      return DateTimeFormatter.ofPattern("y-MM-dd'T'HH:mm:ss.SSSXXX")
    case DateFormat.MonthDayYearShort:
      return DateTimeFormatter.ofPattern('MMM dd, y')
    case DateFormat.UniversalDate:
      return DateTimeFormatter.ofPattern('yyyy-MM-dd')
    case DateFormat.JavaScriptDateString:
      return DateTimeFormatter.ofPattern("eee MMM dd yyyy HH:mm:ss 'GMT'xxx")
    default:
      return assertUnreachable(desiredFormat)
  }
}
